import { ancestralCost, formatNumber, visibleAncestral } from '../../engine';
import { gameStore } from '../../game/store';
import type { Snapshot } from '../../game/store';
import { EffectChips } from '../Chips';
import { Glyph } from '../Glyph';

interface AncestralPanelProps {
  snapshot: Snapshot;
}

export function AncestralPanel({ snapshot }: AncestralPanelProps) {
  const { state } = snapshot;
  const nodes = visibleAncestral(state);
  const notation = state.settings.notation;

  // Group by the age that opened the node — the tree reads as strata of memory.
  const tiers = new Map<number, typeof nodes>();
  for (const node of nodes) {
    const tier = tiers.get(node.def.minAge) ?? [];
    tier.push(node);
    tiers.set(node.def.minAge, tier);
  }
  const tierKeys = [...tiers.keys()].sort((a, b) => a - b);

  return (
    <section className="panel">
      <h2 className="panel-title">
        Ancestral Memory
        <span className="hint">
          {formatNumber(state.ancestralKnowledge, notation)} Knowledge held
        </span>
      </h2>

      <p className="card-desc" style={{ marginBottom: 14 }}>
        What the line keeps when everything else is lost. These never reset.
      </p>

      {tierKeys.map((tier) => (
        <div key={tier} className="ancestral-tier">
          <div className="ancestral-tier-label">
            {tier === 0 ? 'From the beginning' : `Remembered from Age ${tier + 1}`}
          </div>

          <div className="card-grid">
            {(tiers.get(tier) ?? []).map((node) => {
              const cost = ancestralCost(state, node.def.id);
              const affordable =
                node.available && !node.atMax && state.ancestralKnowledge >= cost;

              return (
                <article
                  key={node.def.id}
                  className={`card${node.level > 0 ? ' owned' : ''}${
                    affordable ? ' affordable' : ''
                  }${node.available || node.level > 0 ? '' : ' locked'}`}
                >
                  <div className="card-head">
                    <Glyph
                      id={node.def.glyph}
                      size={24}
                      tone={node.level > 0 ? 'moss' : 'ochre'}
                    />
                    <span className="card-name">{node.def.name}</span>
                    <span className="node-level">
                      {node.level}/{node.def.maxLevel}
                    </span>
                  </div>

                  <p className="card-desc">{node.def.description}</p>
                  <EffectChips effects={node.def.effects} level={Math.max(1, node.level)} />

                  {node.missingRequirements.length > 0 ? (
                    <p className="card-note">Needs {node.missingRequirements.join(', ')}</p>
                  ) : null}

                  <div className="card-actions">
                    <button
                      type="button"
                      className={`btn${affordable ? ' primary' : ''}`}
                      disabled={!affordable}
                      onClick={() => gameStore.remember(node.def.id)}
                    >
                      {node.atMax ? 'Fully remembered' : `Remember — ${formatNumber(cost, notation)}`}
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      ))}
    </section>
  );
}
